import { useState, useRef, useEffect } from "react";
import { Mic, Square, Loader2, Volume2, Sparkles, Check, Plus, RotateCcw, Accessibility } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATES = { IDLE: "idle", RECORDING: "recording", TRANSCRIBING: "transcribing", REFINING: "refining", DONE: "done" };

export default function VoiceRefine({ baseCv, onNeedCv, onApplied }) {
  const [phase, setPhase] = useState(STATES.IDLE);
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [addSummary, setAddSummary] = useState(true);
  const [applying, setApplying] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (audioRef.current) { audioRef.current.pause(); }
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => handleStop();
      mediaRef.current = mr;
      mr.start();
      setPhase(STATES.RECORDING);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access is needed. Please allow it and try again.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 800) { setPhase(STATES.IDLE); return toast.error("That was too short. Try speaking for a bit longer."); }
    setPhase(STATES.TRANSCRIBING);
    try {
      const form = new FormData();
      form.append("file", blob, "answer.webm");
      const { data } = await api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(data.transcript);
      await refine(data.transcript);
    } catch (e) {
      setPhase(STATES.IDLE);
      toast.error(e?.response?.data?.detail || "Couldn't hear that. Please try again.");
    }
  };

  const refine = async (text) => {
    setPhase(STATES.REFINING);
    try {
      const { data } = await api.post("/voice/refine", { transcript: text, context });
      setResult(data);
      setSelected(Object.fromEntries((data.bullets || []).map((_, i) => [i, true])));
      setAddSummary(!!data.summary_line);
      setPhase(STATES.DONE);
      speak(data.coach_message);
    } catch (e) {
      setPhase(STATES.IDLE);
      toast.error(e?.response?.data?.detail || "Couldn't polish that just now.");
    }
  };

  const speak = async (text) => {
    if (!text) return;
    try {
      setSpeaking(true);
      const res = await api.post("/voice/tts", { text, voice: "nova" }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch (e) {
      setSpeaking(false);
      toast.error("Couldn't play the audio.");
    }
  };

  const readBullets = () => {
    if (!result) return;
    const chosen = (result.bullets || []).filter((_, i) => selected[i]);
    if (!chosen.length) return toast.error("Tick at least one point to read aloud.");
    speak("Here are your polished points. " + chosen.join(". "));
  };

  const apply = async () => {
    if (!baseCv) { toast.error("Upload your master CV first."); return onNeedCv?.(); }
    const bullets = (result.bullets || []).filter((_, i) => selected[i]);
    if (!bullets.length && !(addSummary && result.summary_line)) return toast.error("Select at least one point to add.");
    setApplying(true);
    try {
      const { data } = await api.post("/voice/apply", { bullets, summary_line: addSummary ? result.summary_line : "" });
      toast.success(`Added ${data.added} point${data.added === 1 ? "" : "s"} to your master CV`);
      onApplied?.();
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add those.");
    } finally {
      setApplying(false);
    }
  };

  const reset = () => {
    if (audioRef.current) audioRef.current.pause();
    setPhase(STATES.IDLE); setTranscript(""); setResult(null); setSelected({}); setSeconds(0);
  };

  const busy = phase === STATES.TRANSCRIBING || phase === STATES.REFINING;

  return (
    <div className="af-fade-up max-w-3xl" data-testid="voice-refine-view">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Talk to Refine Your CV</h1>
        <span className="flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-widest text-brand bg-brand-soft border border-brand/30 px-2 py-0.5 rounded-full">
          <Accessibility className="w-3 h-3" /> Say it, don't write it
        </span>
      </div>
      <p className="text-body mb-8">Struggle with writing? Just <strong className="text-ink">talk</strong> about a job or achievement and we'll turn your words into polished, ATS-ready CV points — and read them back to you.</p>

      {/* Context (optional) */}
      <div className="rounded-xl border border-line bg-surface p-4 mb-5">
        <label className="text-xs font-mono2 uppercase tracking-widest text-faint">What role are you aiming for? (optional)</label>
        <input
          data-testid="voice-context-input"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="e.g. Customer service, warehouse, care assistant…"
          className="mt-2 w-full bg-canvas border border-line-strong rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors"
        />
      </div>

      {/* Recorder */}
      <div className="rounded-2xl border border-line bg-surface p-8 text-center">
        {phase === STATES.RECORDING ? (
          <>
            <button
              data-testid="voice-stop-button"
              onClick={stopRecording}
              className="relative w-20 h-20 mx-auto rounded-full bg-danger flex items-center justify-center hover:bg-danger/90 transition-colors"
            >
              <span className="absolute inset-0 rounded-full bg-danger/40 animate-ping" />
              <Square className="w-7 h-7 text-onbrand fill-current relative" />
            </button>
            <p className="mt-4 font-medium text-ink">Listening… tap to stop</p>
            <p className="text-sm text-faint font-mono2 mt-1">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</p>
          </>
        ) : busy ? (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-brand-soft border border-brand/30 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-brand animate-spin" />
            </div>
            <p className="mt-4 font-medium text-ink">{phase === STATES.TRANSCRIBING ? "Writing down what you said…" : "Polishing into CV points…"}</p>
          </>
        ) : (
          <>
            <button
              data-testid="voice-record-button"
              onClick={startRecording}
              className="w-20 h-20 mx-auto rounded-full bg-brand flex items-center justify-center hover:bg-brand-hover transition-colors shadow-lg"
            >
              <Mic className="w-8 h-8 text-onbrand" />
            </button>
            <p className="mt-4 font-medium text-ink">{result ? "Record again" : "Tap and start talking"}</p>
            <p className="text-sm text-faint mt-1">Speak naturally — no need for fancy words.</p>
          </>
        )}
      </div>

      {transcript && (
        <div className="mt-6 rounded-xl border border-line bg-surface p-5" data-testid="voice-transcript">
          <div className="text-xs font-mono2 uppercase tracking-widest text-faint mb-2">What you said</div>
          <p className="text-sm text-body whitespace-pre-wrap leading-relaxed">{transcript}</p>
        </div>
      )}

      {result && phase === STATES.DONE && (
        <div className="mt-6 rounded-xl border border-line border-l-[3px] border-l-brand bg-surface p-5" data-testid="voice-result">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-brand font-semibold">
              <Sparkles className="w-4 h-4" /> Your polished CV points
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="voice-play-coach-button"
                onClick={() => speak(result.coach_message)}
                disabled={speaking}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-body border border-line hover:bg-subtle transition-colors disabled:opacity-50"
              >
                {speaking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />} Coach
              </button>
              <button
                data-testid="voice-read-bullets-button"
                onClick={readBullets}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-body border border-line hover:bg-subtle transition-colors"
              >
                <Volume2 className="w-3.5 h-3.5" /> Read points
              </button>
            </div>
          </div>

          {result.summary_line && (
            <label className="flex items-start gap-3 rounded-lg border border-line bg-canvas p-3 mb-3 cursor-pointer" data-testid="voice-summary-toggle">
              <input type="checkbox" checked={addSummary} onChange={(e) => setAddSummary(e.target.checked)} className="mt-1 accent-brand" />
              <div>
                <div className="text-xs font-mono2 uppercase tracking-widest text-warning mb-0.5">Summary line</div>
                <div className="text-sm text-ink">{result.summary_line}</div>
              </div>
            </label>
          )}

          <div className="space-y-2">
            {(result.bullets || []).map((b, i) => (
              <label key={i} data-testid={`voice-bullet-${i}`} className="flex items-start gap-3 rounded-lg border border-line bg-canvas p-3 cursor-pointer hover:border-brand/40 transition-colors">
                <input type="checkbox" checked={!!selected[i]} onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))} className="mt-1 accent-brand" />
                <span className="text-sm text-ink">{b}</span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              data-testid="voice-apply-button"
              onClick={apply}
              disabled={applying}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to my Master CV
            </button>
            <button
              data-testid="voice-reset-button"
              onClick={reset}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-body border border-line hover:bg-subtle transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
